import React, { useState, useEffect } from "react";
import { api } from "../api/api";
import { useNavigate, Link } from "react-router-dom";
import Swal from "sweetalert2";

const UserSidebar = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [user, setUser] = useState(null);

  const navigate = useNavigate();

  // Check if user is logged in on component mount
  useEffect(() => {
    api.get("/users/checklogin", { withCredentials: true }).then((response) => {
      setUser(response.data.user.user);
      setIsLoggedIn(true);
    }).catch(() => setUser(null));
  }, []);

  const handleSignOut = () => {
    api.get("/users/logout", { withCredentials: true });
    setIsLoggedIn(false);
    setIsSidebarOpen(false);
    navigate("/");
  };

  const toggleSidebar = () => setIsSidebarOpen(!isSidebarOpen);
  const closeSidebar = () => setIsSidebarOpen(false);

  const itemClass =
    "group flex items-center gap-3 mx-3 px-3 py-2.5 rounded-xl text-sm font-medium text-gray-700 hover:bg-[#6f48e5]/10 hover:text-[#6f48e5] transition-all duration-150";
  const iconClass =
    "w-5 h-5 shrink-0 text-[#6f48e5]/70 group-hover:text-[#6f48e5] transition-colors";

  const initials = user
    ? `${(user.firstName || "").charAt(0)}${(user.lastName || "").charAt(0)}`.toUpperCase()
    : "";

  return (
    <div>
      {/* Button to toggle sidebar */}
      <button
        onClick={toggleSidebar}
        className="z-30 p-2 rounded-lg text-black hover:text-[#6f48e5] focus:outline-none flex items-center space-x-3 rtl:space-x-reverse transition-transform duration-300 hover:scale-105"
        aria-label="Open menu"
      >
        <svg className="w-5 h-5" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 17 14">
          <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M1 1h15M1 7h15M1 13h15" />
        </svg>
      </button>

      {/* Sidebar */}
      <div
        className={`fixed left-0 top-0 h-full w-72 bg-white shadow-2xl z-20 flex flex-col transform transition-transform duration-200 ease-in-out overflow-x-hidden ${
          isSidebarOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        {/* Sidebar Header */}
        <div className="relative bg-gradient-to-br from-[#6f48e5] to-[#8b6bef] px-5 py-7 text-white">
          <button
            onClick={closeSidebar}
            className="absolute top-3 right-3 p-1.5 rounded-lg text-white/80 hover:text-white hover:bg-white/15 transition-colors"
            aria-label="Close menu"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-12 h-12 rounded-full bg-white/20 text-lg font-bold">
              {isLoggedIn && initials ? (
                initials
              ) : (
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                </svg>
              )}
            </div>
            <div className="min-w-0">
              <div className="font-semibold text-lg leading-tight truncate">
                {isLoggedIn && user ? `${user.firstName} ${user.lastName}` : "Guest User"}
              </div>
              <div className="text-sm text-white/85 truncate">
                {isLoggedIn && user ? user.email : "Login to view details"}
              </div>
            </div>
          </div>
        </div>

        {/* Sidebar Menu */}
        <ul className="py-4 flex-1 overflow-y-auto overflow-x-hidden">
          {/* Profile */}
          {isLoggedIn ? (
            <li>
              <Link to="/profile" onClick={closeSidebar} className={itemClass}>
                {/* ID card / profile icon */}
                <svg className={iconClass} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0ZM4.501 20.118a7.5 7.5 0 0 1 14.998 0A17.933 17.933 0 0 1 12 21.75c-2.676 0-5.216-.584-7.499-1.632Z" />
                </svg>
                Profile
              </Link>
            </li>
          ) : (
            <li>
              <button
                type="button"
                onClick={() => Swal.fire({ icon: "warning", title: "Please Sign in", text: "You need to sign in to view profile" })}
                className={`${itemClass} w-full text-left`}
              >
                <svg className={iconClass} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0ZM4.501 20.118a7.5 7.5 0 0 1 14.998 0A17.933 17.933 0 0 1 12 21.75c-2.676 0-5.216-.584-7.499-1.632Z" />
                </svg>
                Profile
              </button>
            </li>
          )}

          {/* Add Vehicle */}
          <li>
            <Link to="/addVehicle" onClick={closeSidebar} className={itemClass}>
              {/* Truck with plus */}
              <svg className={iconClass} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 18.75a1.5 1.5 0 0 1-3 0m3 0a1.5 1.5 0 0 0-3 0m3 0h6m-9 0H3.375a1.125 1.125 0 0 1-1.125-1.125V14.25m17.25 4.5a1.5 1.5 0 0 1-3 0m3 0a1.5 1.5 0 0 0-3 0m3 0h1.125c.621 0 1.129-.504 1.09-1.124a17.902 17.902 0 0 0-3.213-9.193 2.056 2.056 0 0 0-1.58-.86H14.25M16.5 18.75h-2.25m0-11.177v-.958c0-.568-.422-1.048-.987-1.106a48.554 48.554 0 0 0-10.026 0 1.106 1.106 0 0 0-.987 1.106v7.635m12-6.677v6.677m0 4.5v-4.5m0 0h-12" />
              </svg>
              Add Vehicle
            </Link>
          </li>

          {/* Browse Vehicles */}
          <li>
            <Link to="/vehicles" onClick={closeSidebar} className={itemClass}>
              {/* Squares/grid = browse */}
              <svg className={iconClass} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6A2.25 2.25 0 0 1 6 3.75h2.25A2.25 2.25 0 0 1 10.5 6v2.25a2.25 2.25 0 0 1-2.25 2.25H6a2.25 2.25 0 0 1-2.25-2.25V6ZM3.75 15.75A2.25 2.25 0 0 1 6 13.5h2.25a2.25 2.25 0 0 1 2.25 2.25V18a2.25 2.25 0 0 1-2.25 2.25H6A2.25 2.25 0 0 1 3.75 18v-2.25ZM13.5 6a2.25 2.25 0 0 1 2.25-2.25H18A2.25 2.25 0 0 1 20.25 6v2.25A2.25 2.25 0 0 1 18 10.5h-2.25a2.25 2.25 0 0 1-2.25-2.25V6ZM13.5 15.75a2.25 2.25 0 0 1 2.25-2.25H18a2.25 2.25 0 0 1 2.25 2.25V18A2.25 2.25 0 0 1 18 20.25h-2.25A2.25 2.25 0 0 1 13.5 18v-2.25Z" />
              </svg>
              Browse Vehicles
            </Link>
          </li>

          {/* Divider */}
          <li className="my-3 mx-3 border-t border-gray-100" aria-hidden="true"></li>

          {/* Guest: Sign Up / Sign In */}
          {!isLoggedIn && (
            <>
              <li>
                <Link to="/signUp" onClick={closeSidebar} className={itemClass}>
                  {/* User plus = sign up */}
                  <svg className={iconClass} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M18 7.5v3m0 0v3m0-3h3m-3 0h-3m-2.25-4.125a3.375 3.375 0 1 1-6.75 0 3.375 3.375 0 0 1 6.75 0ZM3 19.235v-.11a6.375 6.375 0 0 1 12.75 0v.109A12.318 12.318 0 0 1 9.374 21c-2.331 0-4.512-.645-6.374-1.766Z" />
                  </svg>
                  Sign Up
                </Link>
              </li>
              <li>
                <Link to="/signIn" onClick={closeSidebar} className={itemClass}>
                  {/* Arrow right into door = sign in */}
                  <svg className={iconClass} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 9V5.25A2.25 2.25 0 0 0 13.5 3h-6a2.25 2.25 0 0 0-2.25 2.25v13.5A2.25 2.25 0 0 0 7.5 21h6a2.25 2.25 0 0 0 2.25-2.25V15m3 0 3-3m0 0-3-3m3 3H9" />
                  </svg>
                  Sign In
                </Link>
              </li>
            </>
          )}

          {/* Logged in: Sign Out */}
          {isLoggedIn && (
            <li>
              <button
                type="button"
                onClick={handleSignOut}
                className="group flex items-center gap-3 mx-3 px-3 py-2.5 rounded-xl text-sm font-semibold text-red-600 hover:bg-red-50 transition-all duration-150 w-full text-left"
              >
                {/* Arrow left out of door = sign out */}
                <svg className="w-5 h-5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 9V5.25A2.25 2.25 0 0 1 10.5 3h6a2.25 2.25 0 0 1 2.25 2.25v13.5A2.25 2.25 0 0 1 16.5 21h-6a2.25 2.25 0 0 1-2.25-2.25V15M12 9l3 3m0 0-3 3m3-3H2.25" />
                </svg>
                Sign Out
              </button>
            </li>
          )}
        </ul>

        {/* Footer */}
        <div className="px-5 py-4 border-t border-gray-100 text-center">
          <p className="text-xs text-gray-400">© {new Date().getFullYear()} HireLanka</p>
        </div>
      </div>

      {/* Overlay to close sidebar when clicking outside */}
      {isSidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-10 transition-opacity duration-200"
          onClick={toggleSidebar}
        ></div>
      )}
    </div>
  );
};

export default UserSidebar;
